// src/api/platformNotificationsApi.ts
//
// CRM BFF notification-preferences API for Settings → Notifications.
//
// Replaces the browser-direct-to-Central preference endpoints of
// `centralNotificationsApi` for the Settings Notifications tab. Requests go to
// the CRM BFF at `/api/platform/notifications/*` via the shared `baseApi`
// (cookie/session auth, boot-gated). The BROWSER NEVER CALLS CENTRAL and needs
// no Central token — the server forwards the user's Central token.
//
// The notification inbox/bell endpoints (unread count, list, mark-read) are ALSO
// served here via the BFF (PR-N1) — the browser holds no Central token in RP/BFF
// mode, so the header bell must go through the CRM BFF too. The server derives
// the workspace from the session, so these hooks take NO workspaceId. Central
// stays authoritative for count/list/grouping/pagination/mark-read; CRM only
// forwards. Realtime is polled here; the same-origin SSE proxy is a PR-N2
// follow-up (see docs/notifications/bell-investigation.md).
//
// Reuses the shared @levelup/central-notifications contracts.

import { baseApi } from './baseApi';
import type {
  PreferencesResponse,
  UpdateCategoryResponse,
  UpdateCategoryParams,
  NotificationChannel,
  GroupedInboxResponse,
  GroupDetailResponse,
  MarkReadResponse,
  MarkBulkReadResponse,
  MarkGroupReadResponse,
} from '@levelup/central-notifications';
// Canonical inbox unread-count shape ({ ok, total, byApp }) — Central does NOT
// return the shared `UnreadCountResponse.unreadCount` field.
import type { InboxUnreadCount } from './notificationInboxTypes';

/**
 * Inbox query params for the BFF bell endpoints. NOTE: no `workspaceId` — the
 * CRM BFF derives it from the authenticated session, so the browser cannot
 * target another workspace.
 */
export interface PlatformInboxParams {
  appId?: string;
  cursor?: string;
  limit?: number;
  grouped?: boolean;
  enriched?: boolean;
}

export const platformNotificationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPlatformNotificationPreferences: builder.query<PreferencesResponse, void>({
      query: () => ({ url: '/platform/notifications/preferences', method: 'GET' }),
    }),

    updatePlatformCategoryPreference: builder.mutation<UpdateCategoryResponse, UpdateCategoryParams>({
      query: ({ category, channels }) => ({
        url: `/platform/notifications/preferences/${encodeURIComponent(category)}`,
        method: 'PATCH',
        body: { channels },
      }),
      // Optimistic update — mirrors the previous centralNotificationsApi UX.
      async onQueryStarted({ category, channels }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          platformNotificationsApi.util.updateQueryData(
            'getPlatformNotificationPreferences',
            undefined,
            (draft) => {
              const pref = draft.preferences.find((p) => p.category === category);
              if (pref) {
                for (const [ch, enabled] of Object.entries(channels)) {
                  const channel = ch as NotificationChannel;
                  if (channel in pref.channels && typeof enabled === 'boolean') {
                    pref.channels[channel].enabled = enabled;
                  }
                }
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    // ---- Inbox / bell (BFF; server-derived workspace; cookie auth) ----
    // Distinct `Notification` tag ids (PLATFORM_*) keep this cross-suite inbox
    // separate from the legacy CRM-local /api/notifications surface.
    getPlatformGroupedInbox: builder.query<GroupedInboxResponse, PlatformInboxParams | void>({
      query: (arg) => {
        const { appId, cursor, limit, grouped = true, enriched = true } = arg ?? {};
        return {
          url: '/platform/notifications/inbox',
          method: 'GET',
          params: {
            ...(grouped ? { grouped: 'true' } : {}),
            ...(enriched ? { enriched: 'true' } : {}),
            ...(appId ? { appId } : {}),
            ...(cursor ? { cursor } : {}),
            ...(limit ? { limit } : {}),
          },
        };
      },
      // Merge cursor pages under a single cache key (Central owns pagination).
      serializeQueryArgs: ({ queryArgs }) => {
        const { cursor, ...rest } = queryArgs ?? {};
        return rest;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg && arg.cursor) {
          return { ...newItems, items: [...currentCache.items, ...newItems.items] };
        }
        return newItems;
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
      providesTags: [{ type: 'Notification', id: 'PLATFORM_INBOX' }],
    }),

    getPlatformInboxGroup: builder.query<GroupDetailResponse, { correlationId: string }>({
      query: ({ correlationId }) => ({
        url: `/platform/notifications/inbox/groups/${encodeURIComponent(correlationId)}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, arg) => [{ type: 'Notification', id: `PLATFORM_GROUP_${arg.correlationId}` }],
    }),

    getPlatformUnreadCount: builder.query<InboxUnreadCount, { appId?: string } | void>({
      query: (arg) => ({
        url: '/platform/notifications/inbox/unread-count',
        method: 'GET',
        params: arg && arg.appId ? { appId: arg.appId } : undefined,
      }),
      providesTags: [{ type: 'Notification', id: 'PLATFORM_UNREAD' }],
    }),

    markPlatformNotificationRead: builder.mutation<MarkReadResponse, string>({
      query: (id) => ({
        url: `/platform/notifications/inbox/${encodeURIComponent(id)}/read`,
        method: 'PATCH',
      }),
      invalidatesTags: [
        { type: 'Notification', id: 'PLATFORM_INBOX' },
        { type: 'Notification', id: 'PLATFORM_UNREAD' },
      ],
    }),

    markPlatformBulkRead: builder.mutation<MarkBulkReadResponse, { appId?: string; notificationIds?: string[] } | void>({
      query: (arg) => ({
        url: '/platform/notifications/inbox/mark-read',
        method: 'POST',
        body: {
          ...(arg && arg.appId ? { appId: arg.appId } : {}),
          ...(arg && arg.notificationIds ? { notificationIds: arg.notificationIds } : {}),
        },
      }),
      invalidatesTags: [
        { type: 'Notification', id: 'PLATFORM_INBOX' },
        { type: 'Notification', id: 'PLATFORM_UNREAD' },
      ],
    }),

    markPlatformGroupRead: builder.mutation<MarkGroupReadResponse, { correlationId: string }>({
      query: ({ correlationId }) => ({
        url: `/platform/notifications/inbox/groups/${encodeURIComponent(correlationId)}/mark-read`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Notification', id: 'PLATFORM_INBOX' },
        { type: 'Notification', id: 'PLATFORM_UNREAD' },
        { type: 'Notification', id: `PLATFORM_GROUP_${arg.correlationId}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPlatformNotificationPreferencesQuery,
  useUpdatePlatformCategoryPreferenceMutation,
  useGetPlatformGroupedInboxQuery,
  useGetPlatformInboxGroupQuery,
  useGetPlatformUnreadCountQuery,
  useMarkPlatformNotificationReadMutation,
  useMarkPlatformBulkReadMutation,
  useMarkPlatformGroupReadMutation,
} = platformNotificationsApi;
